import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { portalFromOrgCode, type Portal } from '../utils/portal';

// Notification preferences per event type and channel, grouped by category.
//
// Stored as { event_id: { email, bell } } on the user, read and written through
// /api/auth/me/notification-preferences. The MAAP app resolves both channels from
// the same map when it sends an event: email is opt-in, the bell is opt-out. Event
// ids are the stable contract with those senders.

// 'bell' is the MAAP app's notification bell, named for the icon users click, so the
// column carries that icon rather than leaving "In App" to be guessed at.
const CHANNEL_ICONS = {
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
} as const;

// Each channel keeps its own switch color, so a row reads at a glance.
const CHANNELS = [
  { key: 'email', label: 'Email', onColor: 'bg-blue-500' },
  { key: 'bell', label: 'Bell', onColor: 'bg-purple-500' },
] as const;

type Channel = (typeof CHANNELS)[number]['key'];
type EventPrefs = Record<Channel, boolean>;
// As it comes back from the API: the { email, bell } map, or the older bare bool.
type StoredPrefs = Record<string, boolean | EventPrefs>;

// `portals`, on either an event or a whole category, limits it to those portals.
// Absent means everyone sees it. Stage-readiness events belong to the facility that
// works that stage, so SSC and PCF each see only their own and orgs see neither.
interface NotificationEvent {
  id: string;
  label: string;
  portals?: Portal[];
}

interface NotificationCategory {
  key: string;
  label: string;
  events: NotificationEvent[];
  portals?: Portal[];
}

const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    key: 'idpr',
    label: 'IDPR',
    events: [
      { id: 'idpr_submitted', label: 'Submitted' },
      { id: 'idpr_status_change', label: 'Status Change (any)' },
      { id: 'idpr_shipped', label: 'Shipped' },
      { id: 'idpr_comment', label: 'Comment Added' },
    ],
  },
  {
    key: 'acn',
    label: 'ACN',
    events: [
      { id: 'acn_submitted', label: 'Submitted' },
      { id: 'acn_status_change', label: 'Status Change (any)' },
      { id: 'acn_approved', label: 'Approved' },
      { id: 'acn_comment', label: 'Comment Added' },
    ],
  },
  {
    key: 'acp',
    label: 'ACP',
    events: [
      { id: 'acp_status_change', label: 'Status Change (any)' },
      { id: 'acp_approved', label: 'Approved' },
      { id: 'acp_expiring_soon', label: 'Expiring Soon (30 days)' },
      { id: 'acp_expired', label: 'Expired' },
      { id: 'acp_comment', label: 'Comment Added' },
    ],
  },
  {
    key: 'cic',
    label: 'CIC',
    events: [
      { id: 'cic_status_change', label: 'Status Change (any)' },
      { id: 'cic_application_review', label: 'Ready for Application Review', portals: ['ssc'] },
      {
        id: 'cic_mail_channel_review',
        label: 'Ready for Mail Channel Verification',
        portals: ['pcf'],
      },
      { id: 'cic_recommend_approval', label: 'Ready for Recommend Approval', portals: ['ssc'] },
      { id: 'cic_government_approval', label: 'Ready for Government Approval', portals: ['ssc'] },
      { id: 'cic_qualified', label: 'Qualified' },
      { id: 'cic_comment', label: 'Comment Added' },
    ],
  },
  // Overdue reminders only go to the org that owes the load report.
  {
    key: 'load_report',
    label: 'Load Report',
    events: [
      { id: 'load_report_submitted', label: 'Submitted' },
      { id: 'load_report_overdue_30', label: 'Overdue 30 Days', portals: ['org'] },
      { id: 'load_report_overdue_60', label: 'Overdue 60 Days', portals: ['org'] },
      {
        id: 'load_report_overdue_90',
        label: 'Overdue 90 Days (submissions blocked)',
        portals: ['org'],
      },
    ],
  },
  // Only for users who can reach MCIC in the app: never PCF, and for an ORG only when
  // its MCIC menu is unlocked. See mcicVisible below.
  {
    key: 'mcic',
    label: 'MCIC',
    events: [
      { id: 'mcic_new_mission', label: 'New Mission' },
      { id: 'mcic_travel_approved', label: 'Travel Approved' },
      { id: 'mcic_stp_approved', label: 'STP Approved' },
      { id: 'mcic_comment', label: 'Comment Added' },
    ],
  },
  // Last card. The parts database is an SSC-only part of the app (its routes are
  // orgTypes: ['ssc']), so nobody else has a use for these.
  {
    key: 'saasm',
    label: 'Parts Database',
    portals: ['ssc'],
    events: [
      { id: 'saasm_new_configuration', label: 'New Configuration Added' },
      { id: 'saasm_comment', label: 'Comment Added' },
    ],
  },
];

// What an event with nothing saved for it means, matching how the MAAP app reads a
// missing key: email is opt-in, the bell is opt-out.
const UNSET: EventPrefs = { email: false, bell: true };

const defaultPrefs = (): Record<string, EventPrefs> =>
  Object.fromEntries(
    NOTIFICATION_CATEGORIES.flatMap((c) => c.events.map((e) => [e.id, { ...UNSET }]))
  );

// A stored value is either the { email, bell } map or, from before channels were
// split, a single bool that applied to both.
const normalize = (stored: unknown): Record<string, EventPrefs> => {
  const prefs = defaultPrefs();
  Object.entries((stored || {}) as Record<string, unknown>).forEach(([id, value]) => {
    if (typeof value === 'boolean') {
      prefs[id] = { email: value, bell: value };
    } else if (value && typeof value === 'object') {
      const v = value as Partial<EventPrefs>;
      prefs[id] = { email: v.email ?? UNSET.email, bell: v.bell ?? UNSET.bell };
    }
  });
  return prefs;
};

export default function NotificationSettings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Record<string, EventPrefs>>(defaultPrefs);
  // What the server had, so a save can leave trimmed-away rows as they were.
  const [stored, setStored] = useState<StoredPrefs>({});
  const [mcicVisible, setMcicVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const portal = portalFromOrgCode(user?.current_organization?.code);
  const inPortal = (portals?: Portal[]) => !portals || (!!portal && portals.includes(portal));

  // Cards and rows the user has no use for are dropped, but their stored preferences
  // are left alone: everything still loads and saves, only the UI is trimmed.
  const visibleCategories = NOTIFICATION_CATEGORIES.filter(
    (c) => inPortal(c.portals) && (c.key !== 'mcic' || mcicVisible)
  ).map((c) => ({ ...c, events: c.events.filter((e) => inPortal(e.portals)) }));

  // Load saved preferences on mount. Stored preferences override the
  // default-on baseline; any event key not present stays opted-in.
  // MCIC access resolves alongside them so the cards render in one pass rather
  // than the MCIC one popping in late.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ preferences }, hasMcic] = await Promise.all([
          apiService.getNotificationPreferences(),
          apiService.hasMcicAccess(),
        ]);
        if (!cancelled) {
          setPrefs(normalize(preferences));
          setStored((preferences || {}) as StoredPrefs);
          setMcicVisible(hasMcic);
        }
      } catch (err) {
        if (!cancelled) setError(apiService.getErrorMessage(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id: string, channel: Channel) => {
    setPrefs((prev) => ({
      ...prev,
      [id]: { ...prev[id], [channel]: !prev[id]?.[channel] },
    }));
    setSuccess('');
  };

  const setCategory = (category: NotificationCategory, value: boolean) => {
    setPrefs((prev) => {
      const next = { ...prev };
      category.events.forEach((e) => {
        next[e.id] = { email: value, bell: value };
      });
      return next;
    });
    setSuccess('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    // Only the rows on screen are saved, so turning everything off can never write an
    // event the user was never shown as on.
    const visible = new Set(visibleCategories.flatMap((c) => c.events.map((e) => e.id)));
    const payload: StoredPrefs = { ...stored };
    Object.entries(prefs).forEach(([id, value]) => {
      if (visible.has(id)) payload[id] = value;
    });
    try {
      await apiService.updateNotificationPreferences(payload);
      setStored(payload);
      setSuccess('Notification preferences saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
        Loading notification preferences...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Notification Preferences</h2>
        <p className="text-sm text-gray-500">
          Choose how you are notified about each event: by email, in the app, or both. You can
          change these at any time.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleCategories.map((category) => {
          const allOn = category.events.every((e) =>
            CHANNELS.every((c) => prefs[e.id]?.[c.key])
          );
          return (
            <div key={category.key} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-5 py-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  {category.label}
                </h3>
                <button
                  type="button"
                  onClick={() => setCategory(category, !allOn)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {allOn ? 'Turn all off' : 'Turn all on'}
                </button>
              </div>
              <div className="flex items-center justify-end gap-4 px-5 pt-2 pb-1">
                {CHANNELS.map((channel) => (
                  <span
                    key={channel.key}
                    className="flex w-9 flex-col items-center gap-0.5 text-xs font-medium uppercase tracking-wide text-gray-400"
                    title={
                      channel.key === 'bell'
                        ? 'The notification bell in MAAP'
                        : 'Email to your account address'
                    }
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={CHANNEL_ICONS[channel.key]}
                      />
                    </svg>
                    {channel.label}
                  </span>
                ))}
              </div>
              <div className="px-5 pb-2 divide-y divide-gray-100">
                {category.events.map((event) => (
                  <div key={event.id} className="flex items-center justify-between gap-3 py-3">
                    <p className="text-sm text-gray-700">{event.label}</p>
                    <div className="flex flex-shrink-0 items-center gap-4">
                      {CHANNELS.map((channel) => {
                        const on = !!prefs[event.id]?.[channel.key];
                        return (
                          <button
                            key={channel.key}
                            type="button"
                            onClick={() => toggle(event.id, channel.key)}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                              on ? channel.onColor : 'bg-gray-300'
                            }`}
                            role="switch"
                            aria-checked={on}
                            aria-label={`${category.label} ${event.label} ${channel.label}`}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                on ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-app btn-app--primary"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
